/**
 * Денежные утилиты фронтенда.
 *
 * Контракт: деньги приходят/отправляются СТРОКОЙ в копейках (поля с суффиксом `Minor`).
 * BigInt — единственный безопасный носитель (Number теряет точность на больших суммах),
 * поэтому парсинг/арифметика идут через BigInt, а Number используется только для UI-нужд
 * (например прогресс-бар), где потеря точности допустима.
 *
 * Отображаем ЦЕЛЫЕ РУБЛИ (копейки округляются к ближайшему рублю), разделитель тысяч —
 * узкий неразрывный пробел, суффикс « ₽».
 */

const NBSP = ' '; // неразрывный пробел (разделитель тысяч и перед знаком валюты)
const MINUS = '−'; // типографский минус

/** Разбор строки копеек в BigInt с валидацией (принимает и bigint как есть). */
function toMinorBigInt(minor: string | bigint): bigint {
  if (typeof minor === 'bigint') return minor;
  const str = minor.trim();
  if (!/^-?\d+$/.test(str)) {
    throw new Error(`Invalid minor amount: "${minor}" (expected integer kopecks string)`);
  }
  return BigInt(str);
}

/** Группировка целой части тысячами через неразрывный пробел: "1234567" → "1 234 567". */
function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/**
 * Копейки (строка) → число рублей (Number).
 * ВНИМАНИЕ: возможна потеря точности на очень больших суммах — использовать только там,
 * где число нужно для UI-вычислений (проценты, ширина бара), НЕ для отображения сумм.
 */
export function minorToRub(minor: string | bigint): number {
  const v = toMinorBigInt(minor);
  return Number(v) / 100;
}

/**
 * Копейки (строка) → отображаемая строка целых рублей: "12345600" → "123 456 ₽".
 * Копейки округляются к ближайшему рублю (банковское округление не требуется — суммы целые).
 */
export function formatRub(minor: string | bigint): string {
  const v = toMinorBigInt(minor);
  const neg = v < 0n;
  const abs = neg ? -v : v;

  // Округление к ближайшему рублю: +50 копеек и целочисленное деление.
  const rub = (abs + 50n) / 100n;
  const grouped = groupThousands(rub.toString());
  return `${neg ? MINUS : ''}${grouped}${NBSP}₽`;
}

/**
 * Пользовательский ввод рублей → строка копеек.
 * Принимает: "12 345", "12345,50", "12345.5", "1 234 567 ₽".
 * Пустой ввод → "0". Невалидный → бросает Error('INVALID_AMOUNT').
 * Знак минус запрещён (суммы траншей/транзакций положительные).
 */
export function rubToMinor(input: string): string {
  const cleaned = input.replace(/[\s ₽]/g, '').replace(',', '.');
  if (cleaned === '') return '0';
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) {
    throw new Error('INVALID_AMOUNT');
  }
  const dot = cleaned.indexOf('.');
  const intPart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fracPart = dot === -1 ? '' : cleaned.slice(dot + 1);
  const frac = (fracPart + '00').slice(0, 2);
  const minor = BigInt(intPart) * 100n + BigInt(frac);
  return minor.toString();
}

/**
 * Копейки → строка рублей с копейками "12345.60" (для точных полей ввода/экспорта, не UI-отображения).
 */
export function minorToRubString(minor: string | bigint): string {
  const v = toMinorBigInt(minor);
  const neg = v < 0n;
  const abs = neg ? -v : v;
  const rub = abs / 100n;
  const kop = (abs % 100n).toString().padStart(2, '0');
  return `${neg ? '-' : ''}${rub.toString()}.${kop}`;
}

/** Безопасное сложение денежных строк (копейки) → строка копеек. */
export function addMinor(a: string | bigint, b: string | bigint): string {
  return (toMinorBigInt(a) + toMinorBigInt(b)).toString();
}

/** Безопасная разность денежных строк (a - b) → строка копеек (может быть отрицательной). */
export function subMinor(a: string | bigint, b: string | bigint): string {
  return (toMinorBigInt(a) - toMinorBigInt(b)).toString();
}

/** Абсолютная величина денежной строки → строка копеек (>=0). */
export function absMinor(a: string | bigint): string {
  const v = toMinorBigInt(a);
  return (v < 0n ? -v : v).toString();
}

/** true, если сумма в копейках равна нулю ("0", "-0", "" трактуется как невалид → бросит). */
export function isZeroMinor(a: string | bigint): boolean {
  return toMinorBigInt(a) === 0n;
}
